import { ButtonGroup, Separator } from "@overmap-ai/blocks"
import { Editor, isTextSelection } from "@tiptap/core"
import { BubbleMenuView } from "@tiptap/extension-bubble-menu"
import { EditorState } from "@tiptap/pm/state"
import { EditorView } from "@tiptap/pm/view"
import { type ComponentProps, memo } from "react"

import { BubbleMenu } from "../../blocks"
import { useFallbackEditor } from "../../hooks"
import {
	BoldToggleButton,
	CodeToggleButton,
	FontSizeButton,
	ItalicToggleButton,
	StrikeToggleButton,
	SubscriptToggleButton,
	SuperscriptToggleButton,
	TextColorButton,
	TextHighlightButton,
	UnderlineToggleButton,
	UnsetMarksButton,
} from "../index"
import { TextColorPickerPopover, TextHighlightPickerPopover } from "../Popovers"

const PLUGIN_KEY = "text-bubble-menu"

type TextBubbleMenuProps = Omit<ComponentProps<typeof BubbleMenu>, "pluginKey">

export const TextBubbleMenu = memo((props: TextBubbleMenuProps) => {
	const { editor: editorFromProps } = props

	const editor = useFallbackEditor(editorFromProps)

	return (
		<BubbleMenu editor={editor} shouldShow={shouldShow} pluginKey={PLUGIN_KEY}>
			<ButtonGroup className="flex items-center gap-0.5 p-1" accentColor="base" variant="ghost" size="sm">
				<BoldToggleButton size="sm" accentColor="base" variant="ghost" />
				<ItalicToggleButton size="sm" accentColor="base" variant="ghost" />
				<UnderlineToggleButton size="sm" accentColor="base" variant="ghost" />
				<StrikeToggleButton size="sm" accentColor="base" variant="ghost" />
				<SubscriptToggleButton size="sm" accentColor="base" variant="ghost" />
				<SuperscriptToggleButton size="sm" accentColor="base" variant="ghost" />
				<CodeToggleButton size="sm" accentColor="base" variant="ghost" />

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<FontSizeButton direction="increase" />
				<FontSizeButton direction="decrease" />

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<TextColorPickerPopover size="xs">
					<TextColorButton size="sm" accentColor="base" variant="ghost" />
				</TextColorPickerPopover>

				<TextHighlightPickerPopover size="xs">
					<TextHighlightButton size="sm" accentColor="base" variant="ghost" />
				</TextHighlightPickerPopover>

				<Separator orientation="vertical" size="sm" className="mx-1" />

				<UnsetMarksButton />
			</ButtonGroup>
		</BubbleMenu>
	)
})

function shouldShow(
	this: BubbleMenuView,
	{ view, state, editor }: { editor: Editor; view: EditorView; state: EditorState },
) {
	const { selection } = state
	const { empty } = selection

	// Only want to show the TextBubbleMenu when the selection within the editor contains text
	const isTextBlock = isTextSelection(state.selection)

	// When clicking on an element inside the bubble menu the editor "blur" event
	// is called and the bubble menu item is focussed. In this case we should
	// consider the menu as part of the editor and keep showing the menu
	const isChildOfMenu = this.element.contains(document.activeElement)
	const hasEditorFocus = view.hasFocus() || isChildOfMenu

	return !(!hasEditorFocus || empty || !isTextBlock || !editor.isEditable || !editor.isFocused)
}
TextBubbleMenu.displayName = "TextBubbleMenu"
